// src/components/Messages.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from './public/PublicLayout';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ARCA Color Scheme
const ARCA_COLORS = {
    primary: '#6B8E23',
    secondary: '#58761C',
    darkGray: '#4A4F4F',
    lightGray: '#A0A5A5',
    success: '#28a745',
    white: '#FFFFFF'
};

const Messages = ({ user, onLogout }) => {
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedMessageIds, setSelectedMessageIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('authToken') ||
                localStorage.getItem('userToken') ||
                localStorage.getItem('directorToken');

            if (!token) {
                setError('Please log in to view your messages');
                setLoading(false);
                navigate('/login');
                return;
            }

            console.log('🔍 Fetching messages with token:', token ? 'Found' : 'Not found');

            // Get user ID from user prop (backend will also verify from token)
            const userId = user?.id;
            console.log('📝 User ID from prop:', userId);

            // Fetch messages directly from the database via API
            const API_BASE = process.env.REACT_APP_API_URL;
            if (!API_BASE) {
                throw new Error('API URL not configured');
            }
            const response = await fetch(`${API_BASE}/messaging/my-messages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            console.log('📡 Response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ Fetch failed:', errorData);
                throw new Error(errorData.message || 'Failed to fetch messages');
            }

            const data = await response.json();
            console.log('📬 Received data:', data);

            // Extract messages from response
            let allMessages = [];

            if (data.success && data.data) {
                if (Array.isArray(data.data.messages)) {
                    allMessages = data.data.messages;
                } else if (Array.isArray(data.data)) {
                    allMessages = data.data;
                } else if (data.data.conversations) {
                    data.data.conversations.forEach(conv => {
                        if (conv.messages && conv.messages.length > 0) {
                            allMessages.push(...conv.messages.map(msg => ({
                                ...msg,
                                conversation_id: conv.id,
                                sender_name: msg.sender_name || (msg.sender_id === userId ? 'You' : 'ARCA Administrator')
                            })));
                        }
                    });
                }
            }

            // Ensure messages have proper sender names (backend should provide sender_name)
            allMessages = allMessages.map(msg => ({
                ...msg,
                sender_name: msg.sender_name || 'ARCA Administrator'
            }));

            // Sort by date (newest first)
            allMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            console.log('✅ Processed messages:', allMessages.length);
            setMessages(allMessages);
            setLoading(false);
        } catch (err) {
            console.error('❌ Error fetching messages:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const handleDeleteMessage = async (messageId) => {
        // Show confirmation toast
        toast.info(
            <div>
                <p style={{ marginBottom: '1rem' }}>Are you sure you want to delete this message?</p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                        style={{
                            backgroundColor: ARCA_COLORS.primary,
                            color: 'white',
                            border: 'none',
                            padding: '0.4rem 1rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                const token = localStorage.getItem('authToken') ||
                                    localStorage.getItem('userToken') ||
                                    localStorage.getItem('directorToken');
                                const API_BASE = process.env.REACT_APP_API_URL;
                                if (!API_BASE) {
                                    throw new Error('API URL not configured');
                                }

                                const response = await fetch(`${API_BASE}/messaging/messages/${messageId}`, {
                                    method: 'DELETE',
                                    headers: { 'Authorization': `Bearer ${token}` }
                                });

                                if (response.ok) {
                                    // Remove message from list
                                    setMessages(messages.filter(msg => msg.id !== messageId));
                                    setSelectedMessageIds(prev => prev.filter(id => id !== messageId));
                                    if (selectedMessage?.id === messageId) {
                                        setSelectedMessage(null);
                                    }
                                    console.log('✅ Message deleted successfully');
                                    toast.success('Message deleted successfully!', {
                                        position: 'top-right',
                                        autoClose: 3000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                        style: {
                                            background: ARCA_COLORS.success,
                                            color: 'white'
                                        }
                                    });
                                } else {
                                    throw new Error('Failed to delete message');
                                }
                            } catch (err) {
                                console.error('❌ Error deleting message:', err);
                                toast.error('Failed to delete message. Please try again.', {
                                    position: 'top-right',
                                    autoClose: 4000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    style: {
                                        background: '#dc3545',
                                        color: 'white'
                                    }
                                });
                            }
                        }}
                    >
                        Yes, Delete
                    </button>
                    <button
                        style={{
                            backgroundColor: ARCA_COLORS.lightGray,
                            color: ARCA_COLORS.darkGray,
                            border: 'none',
                            padding: '0.4rem 1rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                position: 'top-center',
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
                closeButton: false,
                style: {
                    background: 'white',
                    color: ARCA_COLORS.darkGray,
                    border: `2px solid ${ARCA_COLORS.primary}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '350px'
                }
            }
        );
    };

    const markAsRead = async (messageId) => {
        try {
            const token = localStorage.getItem('authToken') ||
                localStorage.getItem('userToken') ||
                localStorage.getItem('directorToken');
            const API_BASE = process.env.REACT_APP_API_URL;
            if (!API_BASE) {
                throw new Error('API URL not configured');
            }

            await fetch(`${API_BASE}/messaging/messages/${messageId}/read`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // Update message status locally
            setMessages(messages.map(msg =>
                msg.id === messageId ? { ...msg, status: 'read' } : msg
            ));
        } catch (err) {
            console.warn('Failed to mark message as read:', err);
        }
    };

    const toggleMessageSelection = (messageId, e) => {
        e?.stopPropagation();
        setSelectedMessageIds(prev =>
            prev.includes(messageId)
                ? prev.filter(id => id !== messageId)
                : [...prev, messageId]
        );
    };

    const toggleSelectAll = () => {
        if (selectedMessageIds.length === messages.length) {
            setSelectedMessageIds([]);
        } else {
            setSelectedMessageIds(messages.map(msg => msg.id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedMessageIds.length === 0) return;

        toast.info(
            <div>
                <p style={{ marginBottom: '1rem' }}>
                    Are you sure you want to delete {selectedMessageIds.length} selected message{selectedMessageIds.length !== 1 ? 's' : ''}?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                        style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '0.4rem 1rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                const token = localStorage.getItem('authToken') ||
                                    localStorage.getItem('userToken') ||
                                    localStorage.getItem('directorToken');
                                const API_BASE = process.env.REACT_APP_API_URL;
                                if (!API_BASE) {
                                    throw new Error('API URL not configured');
                                }

                                const response = await fetch(`${API_BASE}/messaging/messages/bulk-delete`, {
                                    method: 'POST',
                                    headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ messageIds: selectedMessageIds })
                                });

                                if (response.ok) {
                                    const data = await response.json();
                                    setMessages(messages.filter(msg => !selectedMessageIds.includes(msg.id)));
                                    if (selectedMessage && selectedMessageIds.includes(selectedMessage.id)) {
                                        setSelectedMessage(null);
                                    }
                                    setSelectedMessageIds([]);
                                    console.log('✅ Bulk delete successful:', data);
                                    toast.success(data.message || 'Messages deleted successfully!', {
                                        position: 'top-right',
                                        autoClose: 3000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                        style: {
                                            background: ARCA_COLORS.success,
                                            color: 'white'
                                        }
                                    });
                                } else {
                                    throw new Error('Failed to delete messages');
                                }
                            } catch (err) {
                                console.error('❌ Error bulk deleting messages:', err);
                                toast.error('Failed to delete messages. Please try again.', {
                                    position: 'top-right',
                                    autoClose: 4000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    style: {
                                        background: '#dc3545',
                                        color: 'white'
                                    }
                                });
                            }
                        }}
                    >
                        Yes, Delete
                    </button>
                    <button
                        style={{
                            backgroundColor: ARCA_COLORS.lightGray,
                            color: ARCA_COLORS.darkGray,
                            border: 'none',
                            padding: '0.4rem 1rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                position: 'top-center',
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
                closeButton: false,
                style: {
                    background: 'white',
                    color: ARCA_COLORS.darkGray,
                    border: `2px solid #dc3545`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '350px'
                }
            }
        );
    };

    if (loading) {
        return (
            <PublicLayout user={user} onLogout={onLogout}>
                <div className="container py-5">
                    <div className="text-center">
                        <div className="spinner-border" style={{ color: ARCA_COLORS.primary }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading your messages...</p>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    if (error) {
        return (
            <PublicLayout user={user} onLogout={onLogout}>
                <div className="container py-5">
                    <div className="alert alert-danger" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                </div>
            </PublicLayout>
        );
    }

    return (
        <>
            <PublicLayout user={user} onLogout={onLogout}>
                <div className="bg-light py-4">
                    <div className="container">
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h1 className="h3 fw-bold mb-2" style={{ color: ARCA_COLORS.darkGray }}>
                                    <i className="bi bi-envelope-fill me-2" style={{ color: ARCA_COLORS.primary }}></i>
                                    My Messages
                                </h1>
                                <p className="text-muted mb-0">
                                    {messages.length} message{messages.length !== 1 ? 's' : ''} in your inbox
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="btn btn-outline-secondary"
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back to Dashboard
                            </button>
                        </div>

                        <div className="row g-4">
                            {/* Messages List */}
                            <div className="col-lg-5">
                                <div className="card border-0 shadow-sm" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                    <div className="card-header border-0 bg-white sticky-top" style={{ borderBottom: `3px solid ${ARCA_COLORS.primary}` }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={messages.length > 0 && selectedMessageIds.length === messages.length}
                                                    onChange={toggleSelectAll}
                                                    title="Select all messages"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <h5 className="mb-0 fw-bold" style={{ color: ARCA_COLORS.primary }}>
                                                    <i className="bi bi-inbox-fill me-2"></i>
                                                    Inbox
                                                </h5>
                                            </div>
                                            {selectedMessageIds.length > 0 && (
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={handleBulkDelete}
                                                    title="Delete selected messages"
                                                >
                                                    <i className="bi bi-trash me-1"></i>
                                                    Delete Selected ({selectedMessageIds.length})
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="list-group list-group-flush">
                                        {messages.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="bi bi-inbox display-1 text-muted mb-3"></i>
                                                <p className="text-muted mb-0">No messages yet</p>
                                                <small className="text-muted">Your messages will appear here</small>
                                            </div>
                                        ) : (
                                            messages.map((message, index) => (
                                                <div
                                                    key={message.id || index}
                                                    className={`list-group-item list-group-item-action border-0 ${selectedMessage?.id === message.id ? 'active' : ''
                                                        }`}
                                                    onClick={() => {
                                                        setSelectedMessage(message);
                                                        if (message.status !== 'read') {
                                                            markAsRead(message.id);
                                                        }
                                                    }}

                                                    style={{
                                                        backgroundColor: selectedMessage?.id === message.id ? ARCA_COLORS.primary : 'transparent',
                                                        color: selectedMessage?.id === message.id ? ARCA_COLORS.white : ARCA_COLORS.darkGray,
                                                        borderLeft: selectedMessage?.id === message.id ? `4px solid ${ARCA_COLORS.secondary}` : '4px solid transparent',
                                                        transition: 'all 0.2s ease',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <div className="d-flex w-100 justify-content-between align-items-start">
                                                        <div className="d-flex align-items-start gap-2 flex-grow-1">
                                                            <input
                                                                className="form-check-input mt-1"
                                                                type="checkbox"
                                                                checked={selectedMessageIds.includes(message.id)}
                                                                onChange={(e) => toggleMessageSelection(message.id, e)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                title="Select message"
                                                            />
                                                            <div className="flex-grow-1 text-start">
                                                                <h6 className="mb-1 fw-bold">
                                                                    <i className={`bi ${message.sender_name === 'You' ? 'bi-person-circle' : 'bi-shield-fill-check'} me-2`}></i>
                                                                    {message.sender_name}
                                                                </h6>
                                                                <p className="mb-1 small" style={{
                                                                    color: selectedMessage?.id === message.id ? 'rgba(255,255,255,0.9)' : ARCA_COLORS.lightGray
                                                                }}>
                                                                    {truncateText(message.content, 60)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <small style={{
                                                            color: selectedMessage?.id === message.id ? 'rgba(255,255,255,0.8)' : ARCA_COLORS.lightGray,
                                                            whiteSpace: 'nowrap',
                                                            marginLeft: '10px'
                                                        }}>
                                                            {formatDate(message.created_at)}
                                                        </small>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Message Detail */}
                            <div className="col-lg-7">
                                <div className="card border-0 shadow-sm" style={{ minHeight: '70vh' }}>
                                    {selectedMessage ? (
                                        <>
                                            <div className="card-header bg-white border-0" style={{ borderBottom: `3px solid ${ARCA_COLORS.primary}` }}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h5 className="mb-1 fw-bold" style={{ color: ARCA_COLORS.primary }}>
                                                            <i className={`bi ${selectedMessage.sender_name === 'You' ? 'bi-person-circle' : 'bi-shield-fill-check'} me-2`}></i>
                                                            {selectedMessage.sender_name}
                                                        </h5>
                                                        <small className="text-muted">
                                                            <i className="bi bi-clock me-1"></i>
                                                            {new Date(selectedMessage.created_at).toLocaleString('en-US', {
                                                                weekday: 'long',
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </small>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteMessage(selectedMessage.id)}
                                                            title="Delete Message"
                                                        >
                                                            <i className="bi bi-trash me-1"></i>
                                                            Delete
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => setSelectedMessage(null)}
                                                            title="Close"
                                                        >
                                                            <i className="bi bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-body p-4" style={{ maxHeight: 'calc(70vh - 100px)', overflowY: 'auto' }}>
                                                <div className="message-content">
                                                    <div
                                                        className="p-4 rounded"
                                                        style={{
                                                            backgroundColor: '#f8f9fa',
                                                            borderLeft: `4px solid ${ARCA_COLORS.primary}`
                                                        }}
                                                    >
                                                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: ARCA_COLORS.darkGray }}>
                                                            {selectedMessage.content}
                                                        </p>
                                                    </div>

                                                    {(() => {
                                                        try {
                                                            const attachments = selectedMessage.attachments
                                                                ? (typeof selectedMessage.attachments === 'string'
                                                                    ? JSON.parse(selectedMessage.attachments)
                                                                    : selectedMessage.attachments)
                                                                : [];
                                                            return attachments && attachments.length > 0 && (
                                                                <div className="mt-4">
                                                                    <h6 className="fw-bold mb-3" style={{ color: ARCA_COLORS.primary }}>
                                                                        <i className="bi bi-paperclip me-2"></i>
                                                                        Attachments
                                                                    </h6>
                                                                    <div className="list-group">
                                                                        {attachments.map((file, idx) => (
                                                                            <div key={idx} className="list-group-item">
                                                                                <i className="bi bi-file-earmark me-2" style={{ color: ARCA_COLORS.primary }}></i>
                                                                                {file.originalName || file.filename}
                                                                                <small className="text-muted ms-2">({(file.size / 1024).toFixed(2)} KB)</small>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        } catch (e) {
                                                            console.warn('Failed to parse attachments:', e);
                                                            return null;
                                                        }
                                                    })()}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="card-body d-flex align-items-center justify-content-center" style={{ minHeight: '70vh' }}>
                                            <div className="text-center">
                                                <i className="bi bi-chat-left-text display-1 mb-3" style={{ color: ARCA_COLORS.lightGray }}></i>
                                                <h5 className="text-muted">Select a message to read</h5>
                                                <p className="text-muted small">Choose a message from the list to view its content</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PublicLayout>
            <ToastContainer
                position="top-center"
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </>
    );
};

export default Messages;
